import { useCallback, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { DateTime } from 'luxon';
import { ApolloCache, FetchResult } from '@apollo/client';
import {
  CreateTimeSignalMutation,
  Label,
  SortOrder,
  TimeSignal,
  TimeSignalClassification,
  TimeSignalFilterInput,
  UpdateTimeSignalsInput,
  useCreateTimeSignalMutation,
  useUpdateTimeSignalsMutation
} from 'graphql/types';
import { useNotesSelectionReset } from 'modules/notes/list/hooks';
import {
  FormTimesheetLabel,
  TimesheetLabel
} from 'modules/timesheet-labels/labelSchema';
import { useNotesToastContext } from 'modules/notes/NotesToastContextProvider';
import { clearCachedDates } from 'modules/notes/cache/utils/time-signal-utils';
import { useMeContext } from 'modules/me/MeContextProvider';
import { mapDateToISOString } from 'modules/../utils/date-time';
import {
  mapTimesheetLabel,
  SearchCriteria,
  truncateMessage
} from 'modules/notes/common';
import { useIsSmartSuggestionSettingEnabled } from 'modules/timesheet-labels/hooks';
import {
  TimeSignalGetParam,
  UpdateFilterParams,
  useTimeSignalGetParam,
  useTimeSignalUpdateFilter
} from 'modules/notes/hooks/useTimeSignalParam';
import {
  NOTES_PAGE_TYPE,
  NotesClassification,
  useNotesFilterContext
} from 'modules/notes/NotesFilterContextProvider';
import { updateCreateSignalCache } from 'modules/notes/cache/utils/cache-utils';
import { useNotesSortContext } from 'modules/notes/NotesSortContextProvider';
import { TIMELINE_TIMESIGNAL_COUNT } from 'modules/notes/Timeline';
import { NotesActions } from 'modules/notes/notesSelector/hooks';
import { useNotesAnimationContext } from 'modules/notes/providers';
import { useFeatureFlag } from 'modules/common/hooks';

type UpdateTimeSignalsArgs = {
  selectedNoteIds: Array<string>;
  unSelectedNoteIds: Array<string>;
  excluded: boolean;
};

const getFilterInput = ({
  selectedNoteIds,
  unSelectedNoteIds,
  excluded,
  cursor,
  selectedFilterFn
}: UpdateTimeSignalsArgs & {
  cursor: string | null;
  selectedFilterFn: (params: UpdateFilterParams) => TimeSignalFilterInput;
}) => {
  const selectionFilter = selectedFilterFn({
    includeIds: selectedNoteIds.length ? selectedNoteIds : undefined,
    excludeIds: unSelectedNoteIds.length ? unSelectedNoteIds : undefined
  });

  const filterInput: UpdateTimeSignalsInput = {
    selectionFilter,
    updates: {
      excluded: excluded
    },
    cursor
  };
  return filterInput;
};

export const updateCreateCache = (
  param: TimeSignalGetParam,
  currentClassification: NotesClassification,
  searchCriteria: SearchCriteria,
  sortOrder: SortOrder
) => (
  cache: ApolloCache<CreateTimeSignalMutation>,
  mutationResult: FetchResult<
    CreateTimeSignalMutation,
    Record<string, any>,
    Record<string, any>
  >
): void => {
  if (mutationResult?.data?.createTimeSignal?.timeSignal) {
    updateCreateSignalCache(
      cache,
      mutationResult?.data?.createTimeSignal?.timeSignal,
      param,
      currentClassification,
      searchCriteria,
      sortOrder
    );
  }
};

export const useGroupEditAction = ({
  getValues,
  notes
}: {
  notes: TimeSignal[];
  getValues: UseFormReturn['getValues'];
}): {
  groupEditAction: (
    onNoteDiscard: () => void,
    classificationTarget: TimeSignalClassification
  ) => void;
  isActionInProgress: boolean;
} => {
  const isSmartSuggestionEnabled = useIsSmartSuggestionSettingEnabled();
  const {
    filterContext: { selectedClassification, searchCriteria, pageType }
  } = useNotesFilterContext();
  const { sortOrder } = useNotesSortContext();
  const param = useTimeSignalGetParam(
    pageType === NOTES_PAGE_TYPE.TIMELINE
      ? TIMELINE_TIMESIGNAL_COUNT
      : undefined
  );
  const { setNotesToAnimate } = useNotesAnimationContext();

  const { setToastContext } = useNotesToastContext();
  const [saving, setSaving] = useState(false);
  const { resetForm } = useNotesSelectionReset();
  const [
    addNote,
    { loading: isCreateTimeSignalLoading, error: createTimeSignalError }
  ] = useCreateTimeSignalMutation();
  const [
    updateTimeSignals,
    { loading: isUpdateTimeSignalsLoading, error: updateTimeSignalsError }
  ] = useUpdateTimeSignalsMutation();
  const { id } = useMeContext() || {};
  const selectedFilterFn = useTimeSignalUpdateFilter();

  const isTimeSignalFederatedSuggestionsEnabled = useFeatureFlag(
    'isTimeSignalFederatedSuggestionsEnabled',
    false
  );
  const groupEditAction = useCallback(
    async (
      onNoteDiscard: () => void,
      classificationTarget: TimeSignalClassification
    ) => {
      setSaving(true);
      const values = getValues();

      const { timesheetLabels, customLabels } = values;
      const tsLabels = ((timesheetLabels ?? []) as FormTimesheetLabel[]).map(
        (l) => l.label
      );
      const labels: Label[] = [...(tsLabels ?? []), ...(customLabels ?? [])];
      const labelValues = labels.map((l: TimesheetLabel) =>
        mapTimesheetLabel(
          isSmartSuggestionEnabled,
          l,
          isTimeSignalFederatedSuggestionsEnabled
        )
      );

      try {
        clearCachedDates(id);
        const note = {
          date: values.date,
          note: values.note,
          classification: classificationTarget,
          startTime: values.startTime,
          endTime: values.endTime,
          duration: values.duration ? values.duration : null,
          timesheetLabels: tsLabels,
          customLabels: customLabels
        };

        const classification =
          note.classification ?? TimeSignalClassification.Unclassified;
        const result = await addNote({
          variables: {
            input: {
              date: note.date
                ? DateTime.fromJSDate(note.date).toISODate()
                : null,
              classification,
              description: note.note,
              startTime: mapDateToISOString(note.startTime),
              endTime: mapDateToISOString(note.endTime),
              duration: note.duration ? note.duration : null,
              labelValues: labelValues
            }
          },
          update: updateCreateCache(
            param,
            selectedClassification,
            searchCriteria,
            sortOrder
          )
        });

        let updateResult = null;
        const notesToExclude = notes.map((note: TimeSignal) => note.id);
        do {
          const input = getFilterInput({
            selectedNoteIds: notesToExclude,
            unSelectedNoteIds: [],
            excluded: true,
            cursor: updateResult?.data?.updateTimeSignals.cursor ?? null,
            selectedFilterFn
          });
          try {
            updateResult = await updateTimeSignals({
              variables: {
                input
              },
              awaitRefetchQueries: true,
              refetchQueries: [
                'timeSignals',
                'timeSignalsCount',
                'timeSignalsTotals'
              ]
            });
          } catch (e) {
            setToastContext({
              showError: true,
              message: 'toast.note.failure'
            });
          }
        } while (updateResult?.data?.updateTimeSignals?.cursor);

        if (!createTimeSignalError && !updateTimeSignalsError) {
          setToastContext({
            showSuccess: true,
            message: 'note.groupEdit.success',
            values: {
              message: truncateMessage(values.note ?? ''),
              count: `${notes.length}`
            }
          });
          if (result.data?.createTimeSignal.timeSignal) {
            setNotesToAnimate([
              {
                id: result.data?.createTimeSignal.timeSignal.id as any,
                action: 'AddNote',
                folder: classification.toString() as NotesActions
              }
            ]);
            setTimeout(() => setNotesToAnimate([]), 2000);
          }
        } else {
          setToastContext({
            showError: true,
            message: 'note.groupEdit.failure'
          });
        }
      } catch (e) {
        setToastContext({
          showError: true,
          message: 'note.groupEdit.failure'
        });
      }

      await onNoteDiscard();
      resetForm();
      setSaving(false);
    },
    [
      addNote,
      createTimeSignalError,
      getValues,
      id,
      isSmartSuggestionEnabled,
      isTimeSignalFederatedSuggestionsEnabled,
      notes,
      param,
      resetForm,
      searchCriteria,
      selectedClassification,
      selectedFilterFn,
      setNotesToAnimate,
      setToastContext,
      sortOrder,
      updateTimeSignals,
      updateTimeSignalsError
    ]
  );

  return {
    groupEditAction,
    isActionInProgress:
      isCreateTimeSignalLoading || isUpdateTimeSignalsLoading || saving
  };
};
